import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Facebook,
  Instagram,
  Search,
  Users,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", fb_id: "", platform: "facebook_group" });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };

  const add = async () => {
    if (!form.name.trim() || !form.fb_id.trim()) return toast.error("Preencha todos os campos");
    try {
      await api.post("/groups", form);
      toast.success("Destino adicionado");
      setForm({ name: "", fb_id: "", platform: "facebook_group" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao adicionar"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este destino?")) return;
    await api.delete(`/groups/${id}`);
    toast.success("Destino removido");
    load();
  };

  const filtered = groups.filter((g) => !q || g.name.toLowerCase().includes(q.toLowerCase()) || g.fb_id.includes(q));

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono-label text-zinc-500 mb-1">Gerenciamento</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Grupos & Páginas</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 h-11" data-testid="groups-add-button">
              <Plus className="h-4 w-4 mr-2" /> Adicionar destino
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-display">Novo destino</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label className="font-mono-label text-zinc-500">Nome</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Empregos Brasil - Vagas CLT"
                  className="rounded-sm mt-1"
                  data-testid="group-form-name"
                />
              </div>
              <div>
                <Label className="font-mono-label text-zinc-500">ID do Facebook/Instagram</Label>
                <Input
                  value={form.fb_id}
                  onChange={(e) => setForm({ ...form, fb_id: e.target.value })}
                  placeholder="Ex: 123456789012345"
                  className="rounded-sm mt-1 font-mono"
                  data-testid="group-form-id"
                />
              </div>
              <div>
                <Label className="font-mono-label text-zinc-500">Tipo</Label>
                <Select
                  value={form.platform}
                  onValueChange={(v) => setForm({ ...form, platform: v })}
                >
                  <SelectTrigger className="rounded-sm mt-1" data-testid="group-form-platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook_group">Grupo do Facebook</SelectItem>
                    <SelectItem value="facebook_page">Página do Facebook</SelectItem>
                    <SelectItem value="instagram">Conta Instagram</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button onClick={add} className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="group-form-submit">Adicionar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Buscar por nome ou ID..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="groups-page-search-input"
              className="pl-10 rounded-sm h-10"
            />
          </div>
          <span className="font-mono-label text-zinc-500">
            <Users className="h-3.5 w-3.5 inline mr-1" />
            {filtered.length} de {groups.length}
          </span>
        </div>
        <div className="divide-y divide-zinc-100">
          {filtered.map((g) => (
            <div key={g.id} className="flex items-center gap-4 px-5 py-3 hover:bg-zinc-50" data-testid={`groups-page-row-${g.id}`}>
              <div className="h-10 w-10 rounded-full bg-zinc-200 flex items-center justify-center text-sm font-bold text-zinc-600">{g.name[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{g.name}</div>
                <div className="text-xs text-zinc-500 font-mono mt-0.5">{g.fb_id}</div>
              </div>
              <PlatformBadge platform={g.platform} />
              <button
                onClick={() => del(g.id)}
                className="h-8 w-8 flex items-center justify-center text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                data-testid={`groups-page-delete-${g.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {filtered.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Nenhum destino encontrado</div>}
        </div>
      </div>
    </div>
  );
}

function PlatformBadge({ platform }) {
  if (platform === "instagram") return <Badge variant="outline" className="rounded-sm border-pink-200 text-pink-700 bg-pink-50"><Instagram className="h-3 w-3 mr-1" /> Instagram</Badge>;
  if (platform === "facebook_page") return <Badge variant="outline" className="rounded-sm border-blue-200 text-blue-700 bg-blue-50"><Facebook className="h-3 w-3 mr-1" /> Página</Badge>;
  return <Badge variant="outline" className="rounded-sm border-zinc-200 text-zinc-700"><Facebook className="h-3 w-3 mr-1" /> Grupo</Badge>;
}
